import { Box, InputAdornment, Tooltip } from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { useState } from 'react';
import {
    formatCurrency,
    isAlphaNumSpacesValid,
    isAlphaNumValid,
    isAlphaOnlyValid,
    isAlphaSpacesOnlyValid,
    isEmailValid,
    isNumberInRange,
    isPhoneNumberValid
} from '../../../common/helper';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning-icon.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/icons/error-icon-banner.svg';

export interface TextBoxProps {
    type?:
        | 'text'
        | 'password'
        | 'number'
        | 'email'
        | 'alphanumeric'
        | 'alphanumericspaces'
        | 'phonenumber'
        | 'alphaonly'
        | 'currency'
        | 'alphaspacesonly';
    icon?: JSX.Element | string;
    iconPlacement?: 'start' | 'end';
    min?: number;
    max?: number;
    step?: number;
    /**
     * When true, error state renders a red exclamation adornment on the input.
     * Hovering the icon shows the helper text in a callout tooltip that
     * flips above or below the icon based on available viewport space.
     * When false (default) the field renders the standard MUI helperText below.
     */
    showTooltipError?: boolean;
    /**
     * When true, the error tooltip is forced open automatically (in addition to hover).
     * Used to auto-surface the message when a row has a single validation error.
     * When undefined/false, the tooltip stays uncontrolled and only opens on hover.
     */
    openTooltipError?: boolean;
    /**
     * When true (and the field is not in an error state), renders a warning exclamation
     * adornment using the warning colour instead of the error colour. Hovering the icon
     * shows `warningText` in the same callout tooltip used for errors.
     */
    warning?: boolean;
    /**
     * Message shown in the warning tooltip (used when `warning` is true).
     */
    warningText?: React.ReactNode;
    /**
     * When true, the warning tooltip is forced open automatically (in addition to hover).
     * Mirrors `openTooltipError` for the warning state.
     */
    openTooltipWarning?: boolean;
}

export const TextboxInput = (props: TextBoxProps & TextFieldProps) => {
    const {
        error,
        onChange,
        type,
        icon,
        iconPlacement,
        hidden,
        min,
        max,
        step = 1,
        showTooltipError = false,
        openTooltipError = false,
        warning = false,
        warningText,
        openTooltipWarning = false,
        helperText,
        onFocus,
        onBlur,
        sx,
        InputProps,
        ...rest
    } = props;
    const [hasError, setHasError] = useState(false);
    // Keep the tooltip controlled at all times so hover keeps working even after
    // `openTooltipError` toggles from true to false (switching a MUI Tooltip between
    // controlled and uncontrolled leaves its internal hover state broken).
    const [tooltipHovered, setTooltipHovered] = useState(false);
    const [focused, setFocused] = useState(false);

    const handleFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(true);
        onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(false);
        onBlur?.(event);
    };

    const isErrored = showTooltipError ? !!error : !!error || hasError;
    const isWarning = !!warning && !isErrored;
    const useTooltip = showTooltipError && isErrored;
    const useWarningTooltip = showTooltipError && isWarning;

    const renderTooltipAdornment = (open: boolean, title: React.ReactNode, icon: React.ReactNode) => (
        <InputAdornment position="end">
            <Tooltip
                arrow
                placement="bottom"
                open={open || tooltipHovered}
                onOpen={() => setTooltipHovered(true)}
                onClose={() => setTooltipHovered(false)}
                title={title ?? ''}
                PopperProps={{
                    popperOptions: {
                        modifiers: [{ name: 'offset', options: { offset: [0, -4] } }]
                    }
                }}
                componentsProps={{
                    tooltip: {
                        sx: (theme) => ({
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.semantic.text.default,
                            border: `1px solid ${theme.palette.semantic.bd.subtle}`,
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
                            fontSize: '14px',
                            padding: '8px 12px',
                            borderRadius: '8px'
                        })
                    },
                    arrow: {
                        sx: (theme) => ({
                            color: theme.palette.background.paper,
                            '&::before': { border: `1px solid ${theme.palette.semantic.bd.subtle}` }
                        })
                    }
                }}
            >
                <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }}>
                    {icon}
                </Box>
            </Tooltip>
        </InputAdornment>
    );

    const prepareInputProps = () => {
        let propset: Record<string, unknown> = { ...(InputProps as object) };
        if (iconPlacement === 'start')
            propset = {
                ...propset,
                startAdornment: <InputAdornment position="start">{icon}</InputAdornment>
            };
        if (iconPlacement === 'end')
            propset = {
                ...propset,
                endAdornment: <InputAdornment position="end">{icon}</InputAdornment>
            };
        if (type === 'number')
            propset = {
                ...propset,
                slotProps: { input: { step } },
                inputProps: { min, max }
            };
        if (useTooltip)
            propset = {
                ...propset,
                endAdornment: renderTooltipAdornment(
                    openTooltipError,
                    helperText,
                    <ErrorIcon />
                )
            };
        else if (useWarningTooltip)
            propset = {
                ...propset,
                endAdornment: renderTooltipAdornment(openTooltipWarning && focused, warningText, <WarningIcon />)
            };
        return propset;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        switch (type) {
            case 'number':
                if (min && max) {
                    setHasError(!isNumberInRange(Number(e.target.value), min, max));
                }
                break;
            case 'email':
                setHasError(!isEmailValid(e.target.value));
                break;
            case 'alphanumeric':
                setHasError(!isAlphaNumValid(e.target.value));
                break;
            case 'alphanumericspaces':
                setHasError(!isAlphaNumSpacesValid(e.target.value));
                break;
            case 'alphaonly':
                setHasError(!isAlphaOnlyValid(e.target.value));
                break;
            case 'alphaspacesonly':
                setHasError(!isAlphaSpacesOnlyValid(e.target.value));
                break;
            case 'phonenumber':
                setHasError(!isPhoneNumberValid(e.target.value));
                break;
            case 'currency':
                setHasError(!formatCurrency(e.target.value));
                break;
            default:
                setHasError(false);
        }
        if (onChange) onChange(e);
    };

    if (hidden) return <></>;

    return (
        <TextField
            {...rest}
            error={isErrored}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            InputProps={prepareInputProps()}
            helperText={showTooltipError ? undefined : helperText}
            sx={(theme) => ({
                '& .MuiOutlinedInput-input': { fontWeight: 400 },
                ...(isErrored && {
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: theme.palette.error.main },
                        '&:hover fieldset': { borderColor: theme.palette.error.main },
                        '&.Mui-focused fieldset': {
                            borderColor: theme.palette.error.main,
                            borderWidth: '1px'
                        }
                    }
                }),
                ...(isWarning && {
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: theme.palette.semantic.bd.warning },
                        '&:hover fieldset': { borderColor: theme.palette.semantic.bd.warning },
                        '&.Mui-focused fieldset': {
                            borderColor: theme.palette.semantic.bd.warning,
                            borderWidth: '1px'
                        }
                    }
                }),
                ...(typeof sx === 'function' ? sx(theme) : (sx as object))
            })}
        />
    );
};
